import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showError } from '@store/logged/actions';
import { FUNDS_OUT } from '@constants/routes';
import { advancementStatus as advancementStatusConstant } from '@constants/advancement';
import { useHistory } from 'react-router';
import {
  fetchAdvancementSimulation,
  setAdvancementStatus,
  fetchAdvancementStatus,
  pushAdvanementsNotification,
} from '@store/advancements/actions';
import { fetchMerchantBalance, fetchMerchantAvailable } from '@store/andes/actions';

import useMovements from '@hooks/useMovements';

import { showBanners, hideBanners } from '@store/common/actions';

import { doAdvancement } from '@services/advancements-service';
import { getEnv } from '@config';
import { isAllowed } from '@helpers/auth';

const useAdvancements = () => {
  const { getMovements } = useMovements();
  const dispatch = useDispatch();
  const history = useHistory();
  const { merchant: loginMerchant } = useSelector((state) => state.login);
  const advancementSimulation = useSelector((state) => state.advancements.advancementSimulation);
  const advancementStatus = useSelector((state) => state.advancements.advancementStatus);
  const { pismoCallTimeout, advancementRequestLimit } = getEnv();

  const canExecuteAdvancement = () =>
    advancementSimulation &&
    advancementSimulation.totals &&
    advancementSimulation.totals.advancement_amount > 0;

  const doGetAdvancementSimulation = useCallback(
    () => dispatch(fetchAdvancementSimulation()),
    // eslint-disable-next-line
    [loginMerchant._id, dispatch]
  );

  const doGetAdvancementStatus = useCallback(
    (advancementStatusData) => dispatch(fetchAdvancementStatus(advancementStatusData)),
    // eslint-disable-next-line
    [dispatch]
  );

  const displayBanners = () => dispatch(showBanners());

  const occultBanners = () => dispatch(hideBanners());

  const refreshDataAdvancement = () => {
    dispatch(fetchMerchantBalance());
    dispatch(fetchMerchantAvailable(loginMerchant._id));
    getMovements({});
    displayBanners();
  };

  const updateAdvancementStatus = (
    newAdvancementStatus = {
      status: null,
      requestId: null,
      requestCounter: 0,
    }
  ) => {
    const { status } = newAdvancementStatus;
    if (status === advancementStatusConstant.PROCESSED) refreshDataAdvancement();

    dispatch(setAdvancementStatus(newAdvancementStatus));
  };

  const checkStatusAdvancement = (advancementStatusData) => {
    const { requestCounter, status } = advancementStatusData;
    if (status === advancementStatusConstant.PENDING) {
      if (requestCounter < advancementRequestLimit) doGetAdvancementStatus(advancementStatusData);
      else {
        updateAdvancementStatus({
          ...advancementStatusData,
          status: advancementStatusConstant.PROCESSED,
        });
        if (isAllowed('SEND_ADVANCEMENT_NOTIFICATION')) {
          pushAdvanementsNotification(advancementStatusData);
        }
      }
    }
  };

  const executeAdvancement = async (liquidationAmount) => {
    updateAdvancementStatus({
      ...advancementStatus,
      status: advancementStatusConstant.PENDING,
      errorCode: null,
    });
    await doAdvancement(liquidationAmount)
      .then((result) => {
        const newAdvancementStatus = {
          ...advancementStatus,
          requestId: result.advancement_request_id,
          status: result.status,
          errorCode: null,
        };
        updateAdvancementStatus(newAdvancementStatus);
        setTimeout(() => checkStatusAdvancement(newAdvancementStatus), pismoCallTimeout);
      })
      .catch((error) => {
        if (error.status === 403 && error.message.code === '1000') {
          history.push(FUNDS_OUT);
        } else {
          const errorCode = error?.message?.code || error?.code || null;
          updateAdvancementStatus({
            ...advancementStatus,
            status: advancementStatusConstant.ERROR,
            errorCode,
          });
          dispatch(showError(error));
        }
      });
  };

  const getIdBackground = () => {
    const { status } = advancementStatus;
    let idBackground;
    switch (status) {
      case null:
        idBackground = canExecuteAdvancement()
          ? 'advance-modal-close-bkgd'
          : 'advance-modal-close-not-enabled-bkgd';
        break;
      case advancementStatusConstant.PENDING:
        idBackground = 'advance-modal-close-pending-bkgd';
        break;
      case advancementStatusConstant.PROCESSED:
        idBackground = 'advance-modal-close-processed-bkgd';
        break;
      default:
        idBackground = 'advance-modal-close-error-bkgd';
    }
    return idBackground;
  };

  return {
    doGetAdvancementSimulation,
    advancementSimulation,
    checkStatusAdvancement,
    canExecuteAdvancement,
    executeAdvancement,
    updateAdvancementStatus,
    getIdBackground,
    refreshDataAdvancement,
    displayBanners,
    occultBanners,
  };
};

export default useAdvancements;
